/* eslint-disable class-methods-use-this */
/* eslint-disable no-underscore-dangle */

import { v4 as uuidv4 } from 'uuid';

import { isReadOnlyMode, tabId } from '../globals';
import { formatError, logError } from '../helpers/logging';
import * as tabManager from '../helpers/tab';
import {
	BaseMessageSenderParams,
	SendMessageResponse,
} from '../message-senders/BaseMessageSender';
import LiveChatService, { LiveChatResponse } from '../services/LiveChatService';
import {
	ActivateLiveChatContentType,
	ChatMessage,
	ChatMessageTypes,
	InputTypes,
	LiveChatAgentStateChangeChatMessage,
	LiveChatStartChatMessage,
} from '../store';

export interface LiveChatSessionInfo {
	participantId: string;
	sessionId: string;
}

export interface PollResponse {
	id: string;
	responses: ChatMessage[];
}

export class LiveChatHandler {
	private liveChatService: LiveChatService;
	private pollCallbackRef?: (response: PollResponse) => void;
	private sessionId: string;
	private participantId: string;
	private _pollIntervalMs: number;
	private pollTimeoutRef?: number;
	private _isUserTyping: boolean;
	private _userInputValue?: string;
	private _isPolling: boolean;
	private _isTransferring: boolean;
	private _prevUserInputValue?: string;
	private _keepAliveLastSent?: number;
	private _keepAliveIntervalMs: number;
	private _errorLogLastFlushed?: number;
	private _errorLogIntervalMs: number;
	private _pollErrorLog: ObjectLiteral[];

	get pollIntervalMs(): number {
		return this._pollIntervalMs;
	}

	get isPolling(): boolean {
		return this._isPolling;
	}

	constructor() {
		this.liveChatService = new LiveChatService(); // TODO: Should this be a singleton?
		this.liveChatService.isReadOnly = isReadOnlyMode;
		this.sessionId = '';
		this.participantId = '';
		this._isUserTyping = false;
		this._isPolling = false;
		this._isTransferring = false;
		this._pollIntervalMs = 2000;
		this._keepAliveIntervalMs = 30000;
		this._errorLogIntervalMs = 30000;
		this._pollErrorLog = [];
	}

	async start(
		customInfo: ActivateLiveChatContentType,
		pollCallback: (response: PollResponse) => void
	): Promise<void> {
		if (this._isPolling) {
			return Promise.resolve();
		}

		this._isPolling = true;
		this.sessionId = customInfo.sessionId;
		this.pollCallbackRef = pollCallback;

		let response: LiveChatResponse;
		try {
			response = await this.liveChatService.start(customInfo);
			if (response.success) {
				const { responses } = response;
				const { content } = responses[0] as LiveChatStartChatMessage;
				this.participantId = content.participantId;

				this.poll();
				this.handleTypingState();

				return Promise.resolve();
			}

			this._isPolling = false;
			return Promise.reject();
		} catch (error) {
			this._isPolling = false;
			return Promise.reject(error);
		}
	}

	pause(): void {
		this._isPolling = false;

		this.flushPollErrorLogs();

		if (this.pollTimeoutRef) {
			clearTimeout(this.pollTimeoutRef);
		}
	}

	async resume(
		sessionInfo: LiveChatSessionInfo,
		pollCallback: (response: PollResponse) => void
	): Promise<void> {
		if (this._isPolling) {
			return;
		}

		this._isPolling = true;
		this.participantId = sessionInfo.participantId;
		this.sessionId = sessionInfo.sessionId;
		this.pollCallbackRef = pollCallback;

		this.poll();
		this.flushPollErrorLogs();
		this.handleTypingState();
	}

	private shouldSendKeepAliveFlag(): boolean {
		if (!this._keepAliveLastSent) {
			return true;
		}
		return Date.now() - this._keepAliveLastSent >= this._keepAliveIntervalMs
			? true
			: false;
	}

	private shouldFlushPollErrorLogs(): boolean {
		if (this._pollErrorLog.length === 0) {
			return false;
		}
		if (!this._errorLogLastFlushed) {
			return true;
		}
		return Date.now() - this._errorLogLastFlushed >= this._errorLogIntervalMs
			? true
			: false;
	}

	private async flushPollErrorLogs() {
		if (this._pollErrorLog.length === 0) {
			return;
		}
		try {
			await logError('Live chat poll errors batch', this._pollErrorLog);
			this._pollErrorLog = [];
			this._errorLogLastFlushed = Date.now();
		} catch (error) {
			// console.log({ error });
		}
	}

	async poll(): Promise<void> {
		if (!this.sessionId || this.sessionId.length === 0) {
			return;
		}

		if (!tabManager.isActiveTab(tabId)) {
			this.pause();
			return;
		}

		const shouldSendKeepAliveFlag = this.shouldSendKeepAliveFlag();
		const shouldFlushPollErrorLogs = this.shouldFlushPollErrorLogs();

		if (shouldFlushPollErrorLogs) {
			this.flushPollErrorLogs();
		}

		const request = {
			participantId: this.participantId,
			sessionId: this.sessionId,
			keepAlive: shouldSendKeepAliveFlag,
		};

		try {
			const response = await this.liveChatService.poll(request);

			if (shouldSendKeepAliveFlag) {
				// Only update when we know the poll was successful
				this._keepAliveLastSent = Date.now();
			}

			if (
				this.pollCallbackRef != null &&
				response.responses &&
				response.responses.length
			) {
				this.pollCallbackRef({ id: uuidv4(), responses: response.responses });
			}

			this.processPollResponse(response);
			// eslint-disable-next-line no-empty
		} catch (error) {
			const errorMessage = formatError(error);

			// Check that the error actually contains a message, as sometimes it is an empty string
			if (errorMessage) {
				this._pollErrorLog.push({
					timestamp: Date.now(),
					error: errorMessage,
				});
			}
		}

		// TODO: Keep polling unless exit from either party
		if (this._isPolling) {
			this.pollTimeoutRef = window.setTimeout(() => {
				this.poll();
				this.handleTypingState();
			}, this._pollIntervalMs);
		} else {
			clearTimeout(this.pollTimeoutRef);
		}
	}

	async sendMessage({
		message,
		inputType = InputTypes.Text,
	}: BaseMessageSenderParams): Promise<SendMessageResponse> {
		const resp = await this.liveChatService.sendMessage({
			participantId: this.participantId,
			sessionId: this.sessionId,
			message,
			inputType,
		});

		this.processPollResponse(resp);

		return Promise.resolve({
			sessionId: this.sessionId,
			responses: resp.responses,
		});
	}

	async exit(shouldCallExitEndpoint = true): Promise<void> {
		const isPollingSnapshot = this._isPolling;
		const sessionIdSnapshot = this.sessionId;
		const participantIdSnapshot = this.participantId;

		this.resetState();

		if (
			isPollingSnapshot &&
			sessionIdSnapshot &&
			participantIdSnapshot &&
			shouldCallExitEndpoint
		) {
			try {
				await this.liveChatService.exit({
					sessionId: sessionIdSnapshot,
					participantId: participantIdSnapshot,
				});
			} catch (error) {
				return Promise.reject();
			}
		}

		return Promise.resolve();
	}

	userDidType(value: string): void {
		if (value !== this._prevUserInputValue) {
			this._userInputValue = value;
		}
	}

	/*
		Decide whether to continue polling or stop based on whether the agent is transferring

		TODO: Write unit tests for these scenarios ..

		Test Scenarios

		#1 Responses [TransferInitated, Disconnected, Connected]
		#2 Responses [TransferInitiated], [Disconnected, Connected]
		#3 Responses [TransferInitiated], [Disconnected], [Connected]
	*/
	private processPollResponse(response: LiveChatResponse) {
		const transferInitiatedResponse = response.responses.find(
			(r) => r.type === ChatMessageTypes.LiveChatAgentTransferInitiate
		);

		this._isTransferring =
			transferInitiatedResponse != null || this._isTransferring;

		const agentStatusChangedResponses = response.responses.filter(
			(r) => r.type === ChatMessageTypes.LiveChatAgentStateChange
		);

		if (agentStatusChangedResponses.length > 0) {
			const mostRecentStatusChange = agentStatusChangedResponses[
				agentStatusChangedResponses.length - 1
			] as LiveChatAgentStateChangeChatMessage;
			if (mostRecentStatusChange.content.state === 'active') {
				this._isPolling = true;
				this._isTransferring = false;
			} else {
				this._isPolling = this._isTransferring;
			}
		}

		if (!this._isPolling) {
			clearTimeout(this.pollTimeoutRef);
		}
	}

	private handleTypingState() {
		if (!this._isUserTyping && this._userInputValue) {
			this.setTypeState(true);
		} else if (
			this._isUserTyping &&
			this._userInputValue === this._prevUserInputValue
		) {
			this.setTypeState(false);
			this._userInputValue = undefined;
		}
		this._prevUserInputValue = this._userInputValue;
	}

	private setTypeState(isTyping: boolean) {
		this._isUserTyping = isTyping;
		this.liveChatService.setTypingState({
			participantId: this.participantId,
			sessionId: this.sessionId,
			typingIndicator: isTyping,
		});
	}

	private resetState(): void {
		this.sessionId = '';
		this.participantId = '';
		this._isUserTyping = false;
		this._userInputValue = undefined;
		this._isPolling = false;
		this._isTransferring = false;
		this._keepAliveLastSent = undefined;
		this._errorLogLastFlushed = undefined;
		this._pollErrorLog = [];
		if (this.pollTimeoutRef) {
			clearTimeout(this.pollTimeoutRef);
		}
	}

	// TODO: Add a destroy method to reset state and reset state
}

export default new LiveChatHandler();
